<div class="space-y-6">

    <!-- KPI Summary Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- 1. Total Transactions Tries -->
        <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
                <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">{{ 'allPayments.kpiTotalTries' | translate }}</p>
                <h3 class="text-2xl font-bold text-slate-900 mt-1">{{ totalTransactionsCount() }}</h3>
                <span class="inline-flex items-center text-xs text-slate-500 font-medium mt-1">
                    <mat-icon class="!w-3 !h-3 !text-[12px]">sync_alt</mat-icon>
                    Direct + sweep attempts
                </span>
            </div>
            <div class="p-3 bg-blue-50 text-blue-600 rounded-xl">
                <mat-icon>receipt_long</mat-icon>
            </div>
        </div>

        <!-- 2. Direct Takings -->
        <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
                <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">{{ 'allPayments.kpiDirectRevenue' | translate }}</p>
                <h3 class="text-2xl font-bold text-slate-900 mt-1">
                    {{ totalDirectTakings() | currency: 'EUR' : 'symbol-narrow' : '1.2-2' }}
                </h3>
                <span class="inline-flex items-center text-xs text-emerald-600 font-medium mt-1">
                    <mat-icon class="!w-3 !h-3 !text-[12px]">point_of_sale</mat-icon>
                    Terminals & mobile apps
                </span>
            </div>
            <div class="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                <mat-icon>credit_card</mat-icon>
            </div>
        </div>

        <!-- 3. Sweep Receivables -->
        <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
                <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">{{ 'allPayments.kpiSweepReceivables' | translate }}</p>
                <h3 class="text-2xl font-bold text-slate-900 mt-1">
                    {{ totalSweepReceivables() | currency: 'EUR' : 'symbol-narrow' : '1.2-2' }}
                </h3>
                <span class="inline-flex items-center text-xs text-amber-600 font-medium mt-1">
                    <mat-icon class="!w-3 !h-3 !text-[12px]">pending_actions</mat-icon>
                    Late payment sweep
                </span>
            </div>
            <div class="p-3 bg-amber-50 text-amber-600 rounded-xl">
                <mat-icon>history_edu</mat-icon>
            </div>
        </div>

        <!-- 4. Settlement Success Rate -->
        <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
                <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">{{ 'allPayments.kpiSuccessRate' | translate }}</p>
                <h3 class="text-2xl font-bold text-slate-900 mt-1">{{ overallSuccessRate() }}</h3>
                <span class="inline-flex items-center text-xs text-indigo-600 font-medium mt-1">
                    <mat-icon class="!w-3 !h-3 !text-[12px]">check_circle</mat-icon>
                    Settled successfully
                </span>
            </div>
            <div class="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                <mat-icon>pie_chart</mat-icon>
            </div>
        </div>
    </div>

    <!-- Filter Strip -->
    <section class="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
        <div class="flex flex-wrap items-end gap-3">
            <!-- Source / Origin Filter -->
            <div class="flex-1 min-w-[180px]">
                <label class="kv-label block mb-1" for="all-pay-source">{{ 'allPayments.source' | translate }}</label>
                <select id="all-pay-source"
                        class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                        [ngModel]="sourceFilter()" (ngModelChange)="sourceFilter.set($event)">
                    <option value="">{{ 'allPayments.allSources' | translate }}</option>
                    @for (s of availableSources; track s.key) {
                    <option [value]="s.key">{{ s.labelKey | translate }}</option>
                    }
                </select>
            </div>

            <!-- Status Filter -->
            <div class="flex-1 min-w-[180px]">
                <label class="kv-label block mb-1" for="all-pay-status">{{ 'allPayments.status' | translate }}</label>
                <select id="all-pay-status"
                        class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                        [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)">
                    <option value="">{{ 'allPayments.allStatuses' | translate }}</option>
                    @for (st of availableStatuses; track st.key) {
                    <option [value]="st.key">{{ st.label }}</option>
                    }
                </select>
            </div>

            <!-- Plate Filter -->
            <div class="flex-1 min-w-[220px]">
                <label class="kv-label block mb-1" for="all-pay-plate">{{ 'allPayments.plate' | translate }}</label>
                <input id="all-pay-plate" type="text"
                       class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                       [placeholder]="'allPayments.platePlaceholder' | translate"
                       [ngModel]="plateFilter()" (ngModelChange)="plateFilter.set($event)"
                       (keyup.enter)="applyFilters()" />
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-2">
                <jjames-button variant="primary" matIconLeft="search" (clicked)="applyFilters()">
                    {{ 'allPayments.apply' | translate }}
                </jjames-button>
                <jjames-button variant="ghost" (clicked)="clearFilters()">
                    {{ 'allPayments.clear' | translate }}
                </jjames-button>
                <jjames-button variant="outline" matIconLeft="download"
                               [loading]="isExporting()" [disabled]="isExporting()"
                               (clicked)="exportCsv()">
                    {{ (isExporting() ? 'allPayments.exporting' : 'allPayments.exportCsv') | translate }}
                </jjames-button>
            </div>
        </div>
    </section>

    <!-- Table Section -->
    <section class="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        @if (isLoading()) {
        <div class="flex items-center justify-center py-16">
            <mat-spinner diameter="32"></mat-spinner>
        </div>
        } @else if (filteredItems().length === 0) {
        <div class="flex flex-col items-center justify-center py-16 text-center px-6">
            <mat-icon class="!w-10 !h-10 !text-[40px] leading-10 text-slate-300">receipt_long</mat-icon>
            <p class="text-sm font-medium text-slate-700 mt-3">{{ 'allPayments.emptyTitle' | translate }}</p>
            <p class="text-sm text-slate-500 mt-1 max-w-md">{{ 'allPayments.emptyBody' | translate }}</p>
        </div>
        } @else {
        <div class="overflow-x-auto">
            <table class="w-full text-sm">
                <thead>
                    <tr class="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50/50">
                        <th class="w-10 px-3 py-3 text-center"></th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'allPayments.colDate' | translate }}</th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'allPayments.colSource' | translate }}</th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'allPayments.colPlate' | translate }}</th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'allPayments.colSpace' | translate }}</th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'allPayments.colStay' | translate }}</th>
                        <th class="px-4 py-3 font-medium text-right whitespace-nowrap">{{ 'allPayments.colAmount' | translate }}</th>
                        <th class="px-4 py-3 font-medium text-right whitespace-nowrap">{{ 'allPayments.colNetPaid' | translate }}</th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'allPayments.colStatus' | translate }}</th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'payments.colReceipt' | translate }}</th>
                    </tr>
                </thead>
                <tbody>
                    @for (item of pagedItems(); track item.id) {
                    <!-- Main Row -->
                    <tr class="border-b border-slate-100 transition-colors cursor-pointer select-none"
                        [class]="rowStatusClass(item)"
                        [class.bg-blue-50_30]="isRowExpanded(item.id)"
                        (click)="toggleRowExpand(item.id)">

                        <!-- Expand Chevron -->
                        <td class="px-3 py-3 text-center text-slate-400">
                            <button type="button"
                                    class="expand-btn p-1 rounded-md hover:bg-slate-200/70 transition-colors focus:outline-none"
                                    [class.is-expanded]="isRowExpanded(item.id)"
                                    (click)="$event.stopPropagation(); toggleRowExpand(item.id)"
                                    [attr.aria-label]="'Toggle details'">
                                <mat-icon class="!w-5 !h-5 !text-[20px] leading-5 transition-transform duration-200 block">
                                    {{ isRowExpanded(item.id) ? 'expand_more' : 'chevron_right' }}
                                </mat-icon>
                            </button>
                        </td>

                        <!-- Date & Time -->
                        <td class="px-4 py-3 whitespace-nowrap text-slate-600">
                            {{ item.date ? (item.date | date:'medium') : '—' }}
                        </td>

                        <!-- Origin / Resource Badge -->
                        <td class="px-4 py-3 whitespace-nowrap">
                            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold"
                                  [class.bg-blue-50]="item.sourceType === 'Stripe'"
                                  [class.text-blue-700]="item.sourceType === 'Stripe'"
                                  [class.bg-purple-50]="item.sourceType === 'CardTerminal'"
                                  [class.text-purple-700]="item.sourceType === 'CardTerminal'"
                                  [class.bg-teal-50]="item.sourceType === 'Im30'"
                                  [class.text-teal-700]="item.sourceType === 'Im30'"
                                  [class.bg-yellow-50]="item.sourceType === 'Coins'"
                                  [class.text-yellow-800]="item.sourceType === 'Coins'"
                                  [class.bg-emerald-50]="item.sourceType === 'Credit'"
                                  [class.text-emerald-700]="item.sourceType === 'Credit'"
                                  [class.bg-amber-50]="item.sourceType === 'Obligation'"
                                  [class.text-amber-800]="item.sourceType === 'Obligation'">
                                <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-3.5">{{ item.sourceIcon }}</mat-icon>
                                {{ item.sourceLabelKey | translate }}
                            </span>
                        </td>

                        <!-- License Plate -->
                        <td class="px-4 py-3 whitespace-nowrap font-medium text-slate-900">
                            <span class="inline-block px-2 py-0.5 bg-slate-100 border border-slate-200 rounded font-mono text-xs font-bold text-slate-800">
                                {{ item.licensePlate || '—' }}
                            </span>
                        </td>

                        <!-- Space Name -->
                        <td class="px-4 py-3 whitespace-nowrap text-slate-600">
                            {{ item.parkingSpaceName || '—' }}
                        </td>

                        <!-- Stay Duration -->
                        <td class="px-4 py-3 whitespace-nowrap text-slate-600">
                            @if (item.stayDuration) {
                            {{ item.stayDuration }}
                            } @else if (item.stayStartedAt) {
                            <span class="attr-pill attr-pill--blue">Still parked</span>
                            } @else {
                            —
                            }
                        </td>

                        <!-- Gross Amount -->
                        <td class="px-4 py-3 whitespace-nowrap text-right text-slate-600 font-medium">
                            {{ item.amount | number:'1.2-2' }} {{ item.currency }}
                        </td>

                        <!-- Net Paid -->
                        <td class="px-4 py-3 whitespace-nowrap text-right font-bold"
                            [class.text-emerald-700]="item.netPaid > 0"
                            [class.text-slate-500]="item.netPaid === 0">
                            {{ item.netPaid | number:'1.2-2' }}
                        </td>

                        <!-- Status Badge -->
                        <td class="px-4 py-3 whitespace-nowrap">
                            <span class="attr-pill" [ngClass]="item.statusBadgeClass">
                                {{ item.statusLabel }}
                            </span>
                        </td>

                        <!-- Receipt Action -->
                        <td class="px-4 py-3 whitespace-nowrap text-left" (click)="$event.stopPropagation()">
                            @if (getReceiptInfo(item); as receipt) {
                            <button type="button"
                                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold shadow-2xs transition-all cursor-pointer border"
                                    [class]="receipt.buttonClass"
                                    (click)="openReceipt(item, $event)"
                                    [title]="receipt.tooltip">
                                <mat-icon class="!w-3.5 !h-3.5 !text-[14px]">{{ receipt.icon }}</mat-icon>
                                <span>{{ receipt.label }}</span>
                                @if (receipt.isExternal) {
                                <mat-icon class="!w-3 !h-3 !text-[12px] opacity-70">open_in_new</mat-icon>
                                }
                            </button>
                            } @else if (item.status.toLowerCase() === 'paid') {
                            <span class="inline-flex items-center gap-1 text-[11px] text-slate-400 font-medium px-2 py-0.5 rounded bg-slate-50 border border-slate-100">
                                <mat-icon class="!w-3 !h-3 !text-[12px]">receipt</mat-icon>
                                {{ 'payments.receiptNone' | translate }}
                            </span>
                            } @else {
                            <span class="text-slate-300">—</span>
                            }
                        </td>
                    </tr>

                    <!-- Expandable Drawer -->
                    @if (isRowExpanded(item.id)) {
                    <tr class="bg-slate-50/90 border-b border-slate-200">
                        <td colspan="10" class="p-4 sm:p-5">
                            <div class="expand-drawer-content space-y-4">
                                <div class="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-200/80">
                                    <div class="flex items-center gap-2 text-xs">
                                        <span class="kv-label">Parking Process ID:</span>
                                        <span class="font-mono text-slate-800 font-semibold">{{ item.parkingProcessId || '—' }}</span>
                                        @if (item.parkingProcessId) {
                                        <button type="button" class="copy-icon-btn" (click)="copyToClipboard(item.parkingProcessId, 'Process ID')" title="Copy Process ID">
                                            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 text-slate-400 hover:text-blue-600">content_copy</mat-icon>
                                        </button>
                                        }
                                    </div>
                                    <div class="flex items-center gap-2 text-xs">
                                        <span class="kv-label">Record Kind:</span>
                                        <span class="font-medium text-slate-700">
                                            {{ item.kind === 'direct_session' ? 'Direct Payment Session' : 'Late-Payment Sweep Obligation' }}
                                        </span>
                                    </div>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <!-- Stay Timing -->
                                    <div class="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs text-xs space-y-1.5">
                                        <div class="flex items-center gap-2 mb-2 text-slate-700 font-semibold uppercase tracking-wide">
                                            <mat-icon class="!w-4 !h-4 !text-[16px] text-blue-600">access_time</mat-icon>
                                            Stay Timing
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-slate-500">Entry:</span>
                                            <span class="font-medium text-slate-800">{{ item.stayStartedAt ? (item.stayStartedAt | date:'medium') : '—' }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-slate-500">Exit:</span>
                                            <span class="font-medium text-slate-800">{{ item.stayEndedAt ? (item.stayEndedAt | date:'medium') : 'Still inside' }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-slate-500">Duration:</span>
                                            <span class="font-semibold text-blue-700">{{ item.stayDuration || 'In progress' }}</span>
                                        </div>
                                    </div>

                                    <!-- Transaction Details -->
                                    <div class="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs text-xs space-y-1.5">
                                        <div class="flex items-center gap-2 mb-2 text-slate-700 font-semibold uppercase tracking-wide">
                                            <mat-icon class="!w-4 !h-4 !text-[16px] text-emerald-600">payment</mat-icon>
                                            Payment Details
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-slate-500">Resource:</span>
                                            <span class="font-semibold text-slate-800">{{ item.sourceLabelKey | translate }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-slate-500">Gross Amount:</span>
                                            <span class="font-medium text-slate-800">{{ item.amount | number:'1.2-2' }} {{ item.currency }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-slate-500">Net Settled:</span>
                                            <span class="font-bold text-emerald-700">{{ item.netPaid | number:'1.2-2' }} {{ item.currency }}</span>
                                        </div>
                                    </div>

                                    <!-- Fiscal Receipt & Invoice -->
                                    <div class="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs text-xs space-y-2">
                                        <div class="flex items-center gap-2 mb-1 text-slate-700 font-semibold uppercase tracking-wide">
                                            <mat-icon class="!w-4 !h-4 !text-[16px] text-indigo-600">verified</mat-icon>
                                            Fiscal & Receipt
                                        </div>
                                        @if (item.receiptUrl) {
                                        <a [href]="item.receiptUrl" target="_blank" rel="noopener noreferrer"
                                           class="inline-flex items-center justify-center gap-1.5 w-full py-1.5 px-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-lg font-bold text-xs transition-colors">
                                            <mat-icon class="!w-3.5 !h-3.5 !text-[14px]">open_in_new</mat-icon>
                                            {{ item.isFiscal ? 'Open EFSTA Bill' : 'Open Stripe Receipt' }}
                                        </a>
                                        } @else if (item.receiptText) {
                                        <button type="button" (click)="showTerminalSlip(item, $event)"
                                                class="inline-flex items-center justify-center gap-1.5 w-full py-1.5 px-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 rounded-lg font-semibold text-xs transition-colors">
                                            <mat-icon class="!w-3.5 !h-3.5 !text-[14px]">receipt_long</mat-icon>
                                            Card Receipt
                                        </button>
                                        } @else {
                                        <p class="text-slate-400 italic text-[11px]">No external fiscal URL stored.</p>
                                        }
                                        @if (item.providerChargeId) {
                                        <div class="pt-1 border-t border-slate-100">
                                            <span class="text-slate-400 text-[10px] block">Reference / Charge ID:</span>
                                            <span class="font-mono text-[10px] text-slate-700 truncate block">{{ item.providerChargeId }}</span>
                                        </div>
                                        }
                                    </div>

                                    <!-- Notes / Reasons -->
                                    <div class="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs text-xs flex flex-col">
                                        <div class="flex items-center gap-2 mb-2 text-slate-700 font-semibold uppercase tracking-wide">
                                            <mat-icon class="!w-4 !h-4 !text-[16px] text-amber-600">notes</mat-icon>
                                            Note & Details
                                        </div>
                                        <div class="flex-1 bg-slate-50 rounded-lg p-2 text-slate-700 border border-slate-100 overflow-y-auto max-h-[80px]">
                                            {{ item.reason || 'No additional note recorded for this transaction attempt.' }}
                                        </div>
                                    </div>
                                </div>

                                <!-- Terminal Capture — only for terminal-of-record payments, so Stripe rows
                                     and sweep obligations keep the four-card layout above unchanged. Full
                                     width because these are the fields a payment dispute is answered with:
                                     the Belegnummer is what the acquirer is given, and the fiscal state is
                                     what says whether the sale was registered for RKSV at all. -->
                                @if (item.terminalCapture; as capture) {
                                <div class="mt-4 bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs text-xs">
                                    <div class="flex items-center gap-2 mb-3 text-slate-700 font-semibold uppercase tracking-wide">
                                        <mat-icon class="!w-4 !h-4 !text-[16px] text-blue-600">point_of_sale</mat-icon>
                                        Terminal Capture
                                        @if (capture.fiscalTestMode) {
                                        <!-- Loud on purpose: on 2026-08-26 five real sales were registered as
                                             TEST bills and nothing on screen said so. -->
                                        <span class="ml-1 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-bold tracking-normal">
                                            TEST FISCAL
                                        </span>
                                        }
                                    </div>

                                    <div class="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2">
                                        <div>
                                            <span class="text-slate-400 text-[10px] block">Beleg-Nr</span>
                                            <span class="font-mono text-slate-800 font-semibold">{{ capture.externalPaymentId || '—' }}</span>
                                        </div>
                                        <div>
                                            <span class="text-slate-400 text-[10px] block">Captured</span>
                                            <span class="text-slate-800">{{ capture.capturedAt ? (capture.capturedAt | date:'medium') : '—' }}</span>
                                        </div>
                                        <div>
                                            <span class="text-slate-400 text-[10px] block">Fiscal Status</span>
                                            <span class="text-slate-800 font-medium">
                                                {{ capture.fiscalReceiptStatus || 'NotRequested' }}
                                                @if (capture.fiscalRegistered) { <span class="text-emerald-700">· device-registered</span> }
                                            </span>
                                        </div>
                                        <div>
                                            <span class="text-slate-400 text-[10px] block">Fiscal Tx</span>
                                            <span class="font-mono text-slate-800">{{ capture.fiscalTransactionId ?? '—' }}</span>
                                        </div>
                                        <div class="col-span-2">
                                            <span class="text-slate-400 text-[10px] block">Terminal</span>
                                            <span class="font-mono text-[10px] text-slate-600 truncate block">{{ capture.paymentTerminalId || '—' }}</span>
                                        </div>
                                        @if (capture.ticketKey) {
                                        <div class="col-span-2">
                                            <span class="text-slate-400 text-[10px] block">Ticket Key</span>
                                            <span class="font-mono text-[10px] text-slate-600 truncate block">{{ capture.ticketKey }}</span>
                                        </div>
                                        }
                                    </div>

                                    <!-- No EFSTA link here on purpose: the fiscal bill already has one in
                                         the row and one in the Fiscal & Receipt card. This panel owns the
                                         other document — the card slip the terminal printed. -->
                                    @if (capture.receiptText) {
                                    <button type="button" (click)="showTerminalSlip(item, $event)"
                                            class="mt-3 inline-flex items-center gap-1.5 py-1.5 px-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 rounded-lg font-semibold text-xs transition-colors">
                                        <mat-icon class="!w-3.5 !h-3.5 !text-[14px]">receipt_long</mat-icon>
                                        Card Receipt
                                    </button>
                                    }
                                </div>
                                }
                            </div>
                        </td>
                    </tr>
                    }
                    }
                </tbody>
            </table>
        </div>

        <div class="border-t border-slate-100 px-4 py-3">
            <jjames-paginator [config]="pagination()" (pageChange)="onPageChange($event)"></jjames-paginator>
        </div>
        }
    </section>

    <!-- Terminal Receipt Preview Modal -->
    @if (selectedReceipt(); as r) {
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150">
        <div class="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full overflow-hidden flex flex-col max-h-[90vh]">
            <div class="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                <div class="flex items-center gap-2.5">
                    <div class="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                        <mat-icon class="!w-5 !h-5 !text-[20px]">receipt</mat-icon>
                    </div>
                    <div>
                        <h3 class="font-bold text-slate-900 text-sm">{{ r.title }}</h3>
                        <p class="text-[11px] text-slate-400 mt-0.5">Physical / Terminal Payment Record</p>
                    </div>
                </div>
                <button type="button" class="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                        (click)="closeReceiptModal()">
                    <mat-icon class="!w-5 !h-5 !text-[20px]">close</mat-icon>
                </button>
            </div>

            <div class="grid grid-cols-2 gap-2 p-4 bg-slate-50 border-b border-slate-100 text-xs">
                @if (r.plate) {
                <div>
                    <span class="text-slate-400 text-[10px] block uppercase font-semibold">Plate</span>
                    <span class="font-mono font-bold text-slate-800">{{ r.plate }}</span>
                </div>
                }
                @if (r.amount !== undefined) {
                <div>
                    <span class="text-slate-400 text-[10px] block uppercase font-semibold">Amount Paid</span>
                    <span class="font-bold text-emerald-700">{{ r.amount | number:'1.2-2' }} {{ r.currency }}</span>
                </div>
                }
                @if (r.date) {
                <div class="col-span-2">
                    <span class="text-slate-400 text-[10px] block uppercase font-semibold">Payment Timestamp</span>
                    <span class="font-medium text-slate-700">{{ r.date }}</span>
                </div>
                }
            </div>

            <div class="p-4 flex-1 overflow-y-auto bg-slate-100 border border-slate-200 font-mono text-xs text-black leading-relaxed rounded-xl m-4 select-text">
                <pre class="whitespace-pre-wrap">{{ r.text || 'No raw receipt slip text available.' }}</pre>
            </div>

            <div class="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-white">
                <button type="button"
                        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                        (click)="copyToClipboard(r.text, 'Receipt text')">
                    <mat-icon class="!w-4 !h-4 !text-[16px]">content_copy</mat-icon>
                    Copy Text
                </button>
                <div class="flex gap-2">
                    <button type="button"
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
                            (click)="printReceiptModal()">
                        <mat-icon class="!w-4 !h-4 !text-[16px]">print</mat-icon>
                        Print
                    </button>
                    <button type="button"
                            class="px-4 py-1.5 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition-colors"
                            (click)="closeReceiptModal()">
                        Close
                    </button>
                </div>
            </div>
        </div>
    </div>
    }
</div>
